/**
 * KnowledgeGaps — TASK-689.
 *
 * "Which facts about this client are missing, and what should I ask?" — the question an RM has
 * before every review meeting and that nothing in Kielis answered.
 *
 * Two design choices carry the feature:
 *
 * 1. **Regulatory and advisory are visually separate.** A list that mixes "no date of birth" in
 *    with "no hobby noted" is noise, and noise is how a compliance tool gets ignored. The
 *    required gaps come first under their own heading, with the file's regulatory coverage.
 * 2. **Every gap carries a question in the client's language, not a field name.** The RM reads
 *    "Which country are you tax resident in?", not `tax.tax_residence` — though the field path
 *    is there too, because they will want to know where it lands.
 *
 * The gap list is a schema-vs-rows diff computed server-side, so nothing here can invent a gap.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Check, Clock, Copy, FileWarning } from 'lucide-react';

import { httpClient } from '../../../lib/httpClient';
import { WidgetContainer } from '../WidgetContainer';
import { WidgetErrorState } from '../../ui/WidgetErrorState';

interface Gap {
  field_path: string;
  domain: string;
  required: boolean;
  kind: 'missing' | 'stale';
  age_days: number | null;
  description?: string | null;
  question: string;
}

interface GapsResponse {
  gaps: Gap[];
  total_gaps: number;
  truncated: boolean;
  missing_required: number;
  missing_optional: number;
  stale: number;
  coverage_pct: number | null;
  required_total: number;
  required_held: number;
  /**
   * What the firm already HOLDS but has not read. A gap list is a diff of the schema against
   * the graph, so without this it says "ask the client" for an answer that may be sitting in a
   * document in the review queue — the portal path, where nothing is extracted until an RM
   * accepts the file.
   */
  documents?: {
    on_file: number;
    pending_review: number;
    unextracted: number;
    ask_client_safe: boolean;
    note: string | null;
  } | null;
}

export interface KnowledgeGapsProps {
  clientId: string;
  title?: string;
}

export function KnowledgeGaps({ clientId, title }: KnowledgeGapsProps) {
  const [copied, setCopied] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['knowledge-gaps', clientId],
    queryFn: () =>
      httpClient.get<GapsResponse>(`/api/knowledge-graph/${clientId}/gaps`, {
        params: { limit: 12 },
      }),
    enabled: Boolean(clientId),
  });

  const data = query.data;
  const required = (data?.gaps ?? []).filter((g) => g.required);
  const optional = (data?.gaps ?? []).filter((g) => !g.required);

  const copyQuestions = async (gaps: Gap[], key: string) => {
    // The RM's actual workflow: take the questions into the meeting.
    await navigator.clipboard?.writeText(gaps.map((g) => `- ${g.question}`).join('\n'));
    setCopied(key);
  };

  const renderGap = (gap: Gap) => (
    <li
      key={gap.field_path}
      data-testid={gap.required ? 'gap-required' : 'gap-optional'}
      className="rounded-md border border-border bg-muted/30 px-2.5 py-2"
    >
      <div className="flex items-start gap-2">
        {gap.kind === 'stale' ? (
          <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
        ) : (
          <AlertCircle
            className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${
              gap.required ? 'text-red-600' : 'text-muted-foreground'
            }`}
          />
        )}
        <div className="min-w-0 flex-1">
          {/* The question the RM says out loud comes first; the field path is secondary. */}
          <p className="text-[13px] text-foreground">{gap.question}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {gap.field_path}
            {gap.kind === 'stale' && gap.age_days != null && (
              <span data-testid="gap-stale-age"> · on file, {gap.age_days} days old</span>
            )}
          </p>
        </div>
      </div>
    </li>
  );

  return (
    <WidgetContainer title={title ?? "What's missing from this file"} source="KYC schema vs knowledge graph">
      {query.isLoading ? (
        <div className="py-8 text-center">
          <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-[#002B45] border-t-transparent" />
        </div>
      ) : query.isError ? (
        <WidgetErrorState
          title="Couldn't scan the file"
          detail="The knowledge graph didn't respond."
          onRetry={() => void query.refetch()}
        />
      ) : !data?.gaps.length ? (
        <p data-testid="gaps-complete" className="py-5 text-center text-sm text-muted-foreground">
          Nothing is missing — every field the schema asks for is on file and current.
        </p>
      ) : (
        <div className="space-y-3">
          {data.coverage_pct != null && (
            <div data-testid="gaps-coverage" className="flex items-center gap-2 text-xs">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                <div
                  className={data.coverage_pct >= 80 ? 'h-full bg-green-600' : 'h-full bg-amber-500'}
                  style={{ width: `${data.coverage_pct}%` }}
                />
              </div>
              <span className="font-medium text-foreground">
                {data.coverage_pct}% regulatory coverage
              </span>
              <span className="text-muted-foreground">
                ({data.required_held}/{data.required_total})
              </span>
            </div>
          )}

          {/* Before any of this is sent to the client: is the answer already in the building? */}
          {data.documents?.note && (
            <div
              data-testid="gaps-documents-note"
              role="note"
              className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900"
            >
              <FileWarning className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{data.documents.note}</span>
            </div>
          )}

          {!!required.length && (
            <div>
              <div className="mb-1 flex items-center justify-between">
                <p className="text-xs font-medium text-red-700">
                  Required — {data.missing_required} missing
                </p>
                <button
                  onClick={() => void copyQuestions(required, 'required')}
                  className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  {copied === 'required' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  copy questions
                </button>
              </div>
              <ul className="space-y-1.5">{required.map(renderGap)}</ul>
            </div>
          )}

          {!!optional.length && (
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">Would help</p>
              <ul className="space-y-1.5">{optional.map(renderGap)}</ul>
            </div>
          )}

          {/* The cap is stated, never silent — a truncated list that looks complete is a lie. */}
          {data.truncated && (
            <p data-testid="gaps-truncated" className="text-[11px] text-muted-foreground">
              Showing {data.gaps.length} of {data.total_gaps} gaps, highest priority first.
            </p>
          )}
        </div>
      )}
    </WidgetContainer>
  );
}

export default KnowledgeGaps;
